const RECOVERY_STORAGE_PREFIX = 'deepmox:dynamic-import-404:';
const HASHED_JAVASCRIPT_ASSET = /^\/assets\/[^/?#]+-[A-Za-z0-9_-]{8,}\.js$/;
const JAVASCRIPT_URL = /https?:\/\/[^\s"'<>]+?\.js(?:\?[^\s"'<>]*)?/g;

interface DynamicImportRecoveryTarget {
	location: {
		origin: string;
		reload(): void;
	};
	sessionStorage: {
		getItem(key: string): string | null;
		setItem(key: string, value: string): void;
	};
	addEventListener(type: string, listener: EventListener): void;
	removeEventListener(type: string, listener: EventListener): void;
}

type RecoveryFetch = (input: string, init?: RequestInit) => Promise<{ status: number }>;

interface VitePreloadErrorEvent extends Event {
	payload?: unknown;
}

function payloadMessage(payload: unknown): string {
	if (payload instanceof Error) return payload.message;
	return typeof payload === 'string' ? payload : '';
}

function findMissingAssetCandidate(payload: unknown, origin: string): string | null {
	const message = payloadMessage(payload);
	for (const match of message.matchAll(JAVASCRIPT_URL)) {
		try {
			const url = new URL(match[0]);
			if (url.origin === origin && HASHED_JAVASCRIPT_ASSET.test(url.pathname)) return url.href;
		} catch {
			// Ignore malformed URLs from unrelated errors.
		}
	}
	return null;
}

export function installDynamicImportRecovery(target: DynamicImportRecoveryTarget, fetcher: RecoveryFetch): () => void {
	const probesInFlight = new Set<string>();
	const onPreloadError = async (event: Event): Promise<void> => {
		const assetUrl = findMissingAssetCandidate((event as VitePreloadErrorEvent).payload, target.location.origin);
		if (!assetUrl) return;

		const storageKey = `${RECOVERY_STORAGE_PREFIX}${assetUrl}`;
		try {
			if (target.sessionStorage.getItem(storageKey) === '1') {
				event.preventDefault();
				return;
			}
		} catch {
			return;
		}

		event.preventDefault();
		if (probesInFlight.has(assetUrl)) return;
		probesInFlight.add(assetUrl);
		try {
			const response = await fetcher(assetUrl, {
				cache: 'no-store',
				credentials: 'same-origin',
				method: 'HEAD',
			});
			if (response.status !== 404) return;
			target.sessionStorage.setItem(storageKey, '1');
			target.location.reload();
		} catch {
			// Network and storage failures are not proof of deployment version skew.
		} finally {
			probesInFlight.delete(assetUrl);
		}
	};

	target.addEventListener('vite:preloadError', onPreloadError as EventListener);
	return () => target.removeEventListener('vite:preloadError', onPreloadError as EventListener);
}
